import { useState, useEffect } from "react";
import { Button, Dropdown, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import CreateChatModal from "../components/Ticket/Modals/CreateTicket";
import UpdateChatModal from "../components/Ticket/Modals/UpdateTicket";
import { formatToTimestamp } from "../utilities/functions";
import PropagateLoader from "react-spinners/PropagateLoader";

interface ChatItem {
  id_chat: number;
  description: string;
  title: string;
  id_user: number;
  username?: string;
  creation_date: string;
  priority: string;
  status: string;
  criticality?: string;
  id_category?: number;
  id_subcategory?: number;
  category_name?: string;
  subcategory_name?: string;
  first_name?: string | null;
  last_name?: string | null;
  id_agent?: number;
}

interface SelectedColumns {
  id_chat: boolean;
  id_agent: boolean;
  description: boolean;
  title: boolean;
  id_user: boolean;
  creation_date: boolean;
  priority: boolean;
  status: boolean;
}

interface ColumnItem {
  key: keyof SelectedColumns;
  label: string;
}

export function Chats() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { translate } = useTranslate();
  const chatIdLabel = "Chat ID";
  const id_user = localStorage.getItem("userid");

  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState<number>(1);
  const [chatList, setChatList] = useState<ChatItem[]>([]);
  const [colum, setSortColum] = useState("id_chat");
  const [sort, setSort] = useState("DESC");
  const [search, setSearch] = useState("");
  const [type, setType] = useState(0);
  const [typeSearch, setTypeSearch] = useState(chatIdLabel);
  const [loading, setLoading] = useState(true);

  const [showCreateChatModal, setShowCreateChatModal] = useState(false);
  const [showUpdateChatModal, setShowUpdateChatModal] = useState(false);
  const [idChat, setIdChat] = useState(0);
  const [idChatToUpdate, setIdChatToUpdate] = useState(0);

  const [selectedColumns, setSelectedColumns] = useState<SelectedColumns>({
    id_chat: true,
    id_agent: true,
    description: false,
    title: true,
    id_user: true,
    creation_date: true,
    priority: true,
    status: true,
  });

  const Columns: Record<string, number> = {
    id_chat: 0,
    agent: 1,
    description: 2,
    id_category: 3,
    id_subcategory: 4,
    title: 5,
    id_user: 6,
    creation_date: 7,
    priority: 8,
    status: 9,
    criticality: 10,
  };

  const handleShowCreateChatModal = () => setShowCreateChatModal(true);
  const handleCloseCreateChatModal = () => setShowCreateChatModal(false);

  const handleOpenUpdateModal = (id: number) => {
    setIdChatToUpdate(id);
    setShowUpdateChatModal(true);
  };

  const handleCloseUpdateChatModal = () => {
    setShowUpdateChatModal(false);
    setIdChatToUpdate(0);
  };

  const getChats = async (
    currentLimit: number,
    currentPage: number,
    currentSearch: string,
    currentType: number,
    currentColumn: string,
    currentSort: string
  ) => {
    try {
      setLoading(true);

      const bodyData = JSON.stringify({
        limit: currentLimit,
        page: currentPage,
        search: currentSearch,
        type: currentType,
        id_user: parseInt(id_user || "0", 10),
        colum: Columns[currentColumn],
        sort: currentSort,
      });

      const totalPagesResponse = await fetch(`${backendUrl}/api/chats/totalpage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyData,
        mode: "cors",
      });

      const totalPagesJson = await totalPagesResponse.json();
      const totalCount = totalPagesJson?.[0]?.count || 0;
      setTotal(totalCount);

      const chatsResponse = await fetch(`${backendUrl}/api/chats/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyData,
        mode: "cors",
      });

      const data = await chatsResponse.json();

      setPageCount(Math.ceil(totalCount / currentLimit));
      setLimit(currentLimit);
      setChatList(Array.isArray(data) ? data : []);

      return data;
    } catch (error) {
      console.error("Error while loading chats:", error);
      setChatList([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const refreshChats = () => {
    getChats(limit, page, search, type, colum, sort);
  };

  const handlePageClick = async (data: { selected: number }) => {
    const selectedPage = data.selected + 1;
    setPage(selectedPage);
    await getChats(limit, selectedPage, search, type, colum, sort);
    window.scrollTo(0, 0);
  };

  const handleSelectChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = parseInt(event.target.value, 10);
    setPage(1);
    setLimit(newValue);
    await getChats(newValue, 1, search, type, colum, sort);
    window.scrollTo(0, 0);
  };

  const handleSortingColum = async (currentColumn: string) => {
    const newSort = colum === currentColumn && sort === "ASC" ? "DESC" : "ASC";
    setSortColum(currentColumn);
    setSort(newSort);
    setPage(1);
    await getChats(limit, 1, search, type, currentColumn, newSort);
  };

  const handleColumnChange = (column: keyof SelectedColumns) => {
    setSelectedColumns((prevState) => ({
      ...prevState,
      [column]: !prevState[column],
    }));
  };

  const handleTypeSearch = (selectedValue: string) => {
    switch (selectedValue) {
      case chatIdLabel:
        setType(0);
        break;
      case translate("Agent"):
        setType(1);
        break;
      case translate("Description"):
        setType(2);
        break;
      case translate("Title"):
        setType(5);
        break;
      case translate("User"):
        setType(6);
        break;
      case translate("Status"):
        setType(7);
        break;
      case translate("Priority"):
        setType(8);
        break;
      default:
        break;
    }

    setTypeSearch(selectedValue);
  };

  const handleAdvancedSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearch(newValue);
    setPage(1);
    await getChats(limit, 1, newValue, type, colum, sort);
  };

  const handleResetSearch = async () => {
    setSearch("");
    setPage(1);
    await getChats(limit, 1, "", type, colum, sort);
  };

  const menuItems = [
    chatIdLabel,
    translate("Agent"),
    translate("Description"),
    translate("Title"),
    translate("Status"),
    translate("Priority"),
  ];

  const displayColumns: ColumnItem[] = [
    { key: "id_chat", label: chatIdLabel },
    { key: "title", label: translate("Title") },
    { key: "description", label: translate("Description") },
    { key: "id_user", label: translate("User") },
    { key: "creation_date", label: translate("Creation date") },
    { key: "status", label: translate("Status") },
    { key: "priority", label: translate("Priority") },
  ];

  useEffect(() => {
    getChats(limit, page, search, type, colum, sort);
  }, []);

  return (
    <>
      <div className="row">
        <div className="col-md-6 col-sm-12">
          <h4>
            <i
              className="lab la-rocketchat"
              data-rel="bootstrap-tooltip"
              title="chat" 
              style={{color:"#ffd02a"}}
            ></i>{" "}
            <span style={{ color: "#000", textTransform: "uppercase" }}>
              chat
            </span>{" "}
            <span style={{ color: "#000" }}>({total})</span>
          </h4>
        </div>

        <div className="col-md-6 col-sm-12 text-right">
          <Button
            variant="primary"
            className="mt-2 mr-1"
            onClick={handleShowCreateChatModal}
          >
            <i className="las la-plus mr-3"></i>
            {translate("Create chat")}
          </Button>
        </div>
      </div>

      <div className="row">
        <div
          className="col-md-4"
          style={{ margin: "0px 0px 10px 0px", padding: "10px" }}
        >
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link" id="dropdown-basic">
                <i className="fas fa-chevron-down" style={{ fontSize: "20" }}></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {menuItems.map((item, index) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => handleTypeSearch(item)}
                    eventKey={item}
                    active={typeSearch === item}
                    className={typeSearch === item ? "select-active" : ""}
                  >
                    {item}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              placeholder={`${translate("Search by")} ${translate(typeSearch)}`}
              onChange={handleAdvancedSearch}
              value={search}
              className="form-control"
            />

            <Button
              variant="secondary"
              onClick={handleResetSearch}
              className="btn-reset"
            >
              <i className="las la-times" style={{ color: "#fff" }}></i>
            </Button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: "0" }}>
              {translate("Show")}
              <select
                name="DataTables_Table_0_length"
                aria-controls="DataTables_Table_0"
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                onChange={handleSelectChange}
                value={limit}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="" id="dropdown-basic" title={translate("Display columns")}>
              <i className="las la-list-ol"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {displayColumns.map((column) => (
                <Dropdown.Item
                  key={column.key}
                  as="button"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns[column.key]}
                    onChange={() => handleColumnChange(column.key)}
                  />
                  <span style={{ marginLeft: "10px" }}>
                    {translate(column.label)}
                  </span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="table-responsive">
        <Table className="dataTable">
          <thead className="text-uppercase">
            <tr className="ligth ligth-data">
              <th>
                <div className="form-check form-check-inline">
                  <input className="form-check-input" type="checkbox" />
                  <label className="form-check-label"></label>
                </div>
              </th>

              {selectedColumns.id_chat && (
                <th className="sorting" onClick={() => handleSortingColum("id_chat")}>
                  N° chat
                </th>
              )}

              {selectedColumns.title && (
                <th className="sorting" onClick={() => handleSortingColum("title")}>
                  {translate("Title")}
                </th>
              )}

              {selectedColumns.description && (
                <th className="sorting" onClick={() => handleSortingColum("description")}>
                  {translate("Description")}
                </th>
              )}

              {selectedColumns.id_agent && (
                <th className="sorting" onClick={() => handleSortingColum("agent")}>
                  {translate("Agent")}
                </th>
              )}

              {selectedColumns.creation_date && (
                <th className="sorting" onClick={() => handleSortingColum("creation_date")}>
                  {translate("Creation Date")}
                </th>
              )}

              {selectedColumns.id_user && (
                <th className="sorting" onClick={() => handleSortingColum("id_user")}>
                  {translate("User")}
                </th>
              )}

              {selectedColumns.status && (
                <th className="sorting" onClick={() => handleSortingColum("status")}>
                  {translate("Status")}
                </th>
              )}

              {selectedColumns.priority && (
                <th className="sorting" onClick={() => handleSortingColum("priority")}>
                  {translate("Priority")}
                </th>
              )}

              <th>{translate("Actions")}</th>
            </tr>
          </thead>

          <tbody className="ligth-body">
            {loading ? (
              <tr>
                <td className="text-center" colSpan={10}>
                  <PropagateLoader color={"#123abc"} loading={loading} size={20} />
                </td>
              </tr>
            ) : chatList.length > 0 ? (
              chatList.map((chat) => (
                <tr key={chat.id_chat}>
                  <td>
                    <div className="form-check form-check-inline">
                      <input type="checkbox" className="form-check-input" />
                    </div>
                  </td>

                  {selectedColumns.id_chat && <td>{chat.id_chat}</td>}
                  {selectedColumns.title && <td>{chat.title}</td>}
                  {selectedColumns.description && <td>{chat.description}</td>}

                  {selectedColumns.id_agent && (
                    <td>
                      {chat.first_name
                        ? `${chat.first_name} ${chat.last_name ?? ""}`.trim()
                        : translate("None")}
                    </td>
                  )}

                  {selectedColumns.creation_date && (
                    <td>{formatToTimestamp(chat.creation_date)}</td>
                  )}

                  {selectedColumns.id_user && (
                    <td>{chat.username || chat.id_user}</td>
                  )}

                  {selectedColumns.status && (
                    <td>{translate(String(chat.status))}</td>
                  )}

                  {selectedColumns.priority && (
                    <td>{translate(String(chat.priority))}</td>
                  )}

                 
                    <td>
                      <div className="d-flex align-items-center list-action">

                        {/* AJOUT ICI */}
                        {(() => {
                          const currentUserId = Number(id_user);
                          const otherUserId =
                            currentUserId === Number(chat.id_user)
                              ? Number(chat.id_agent)
                              : Number(chat.id_user);

                          return (
                            <Link
                              to={`/chat/${chat.id_chat}/${otherUserId}`}
                              className="badge badge-success mr-2"
                              data-toggle="tooltip"
                              data-placement="top"
                              title={translate("Detail")}
                            >
                              <i className="las la-comment-dots" style={{ fontSize: "1.2em" }}></i>
                            </Link>
                          );
                        })()}

                        <span
                          onClick={() => handleOpenUpdateModal(chat.id_chat)}
                          className="badge bg-warning mr-2"
                          data-toggle="tooltip"
                          title={translate("Update")}
                        >
                          <i className="las la-edit"></i>
                        </span>

                      </div>
                   
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11}>No chat available</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {chatList.length} {translate("out")} {total}
          </span>
        </div>

        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={page - 1}
          />
        </div>
      </div>

      <CreateChatModal
        show={showCreateChatModal}
        onHide={handleCloseCreateChatModal}
        refreshChats={refreshChats}
        id_chat={idChat}
      />

      <UpdateChatModal
        show={showUpdateChatModal}
        onHide={handleCloseUpdateChatModal}
        refreshChats={refreshChats}
        id_chat={idChatToUpdate}
      />
    </>
  );
}